// client/src/components/pages/Geofencing/useTrackingFeed.ts
//
// The LexiStar tracking feed shared by Live View (/lexistar/live) and the Geofences page
// (/geofencing): the zones, the zone events that arrive live and the Pending Alerts / Events
// Today counts, kept live by the /tracking socket. With `live: true` it also keeps the vehicles moving: the map
// positions (socket, with a 30 s fallback poll — TRK-5) and the richer /fleet overview behind
// the vehicle rail and detail panel (30 s poll, patched by the socket between polls).
//
// Lifted out of Geofencing/index.tsx when its Live Map tab became the Live View page
// (ST-00210). The loads, polls and socket handlers are the ones the tab had.
//
// ST-00269 (the legacy geofence evaluator is retired): the events and counts are LexiStar's.
// The counts come from GET /api/geofence-events/summary (on load and every 30 s, so they
// follow acknowledgements made elsewhere and the date past midnight); zone events arriving as
// `lexistar:event` on the same /tracking socket (the LexiStar relay) count up between polls
// and are handed to the Events tab, which loads its own filtered list. The legacy
// geofence:entry / exit / dwell socket events are gone. Only these zone-event parts changed;
// the vehicle positions and the fleet overview are as before.
//
// ST-00282: Acknowledge all (acknowledgeAll: the filters the Events tab's list was loaded with,
// its snapshot and the count the user confirmed; the server does the rest) and
// `lexistar:events_acknowledged` on the same socket, which the server sends once an
// acknowledgement has committed. It is a HINT: the counts are re-read from the server, the live
// events it names are shown acknowledged, and `acknowledgedHint` changes so the Events tab
// re-reads its list (nothing is guessed from the message).
import { useCallback, useEffect, useRef, useState } from 'react';
import { connectSocket as connectSocketJs } from '../../../services/socketClient';
import usePollingJs from '../../../hooks/usePolling';
import api from '../../../services/api';
import { isPendingAlert, isZoneEventMessage, sastDayOf, zoneEventFromLive } from './zoneEvents';
import type {
  ConnectSocketFn,
  EventsAcknowledgedMessage,
  FleetResponse,
  FleetVehicle,
  GeofenceListResponse,
  GeofenceRow,
  LexiStarEventMessage,
  LiveVehiclePosition,
  LiveVehiclesResponse,
  VehiclePositionMessage,
  ZoneAlertsAcknowledgeAllBody,
  ZoneAlertsAcknowledgeAllResponse,
  ZoneEvent,
  ZoneEventAcknowledgeResponse,
  ZoneEventSummaryResponse,
} from '../../../types/clientPages/geofencing';

// Typed views of the JavaScript modules this hook uses.
const connectSocket = connectSocketJs as unknown as ConnectSocketFn;
const usePolling = usePollingJs as unknown as (
  callback: () => void,
  intervalMs: number,
  options?: { enabled?: boolean; immediate?: boolean }
) => void;

/** Both pages poll every 30 seconds; the socket fills the gaps. */
export const TRACKING_POLL_MS = 30000;

/** ST-00282: the last `lexistar:events_acknowledged` hint (nonce 0: none yet). */
export interface AcknowledgedHint {
  nonce: number;
  ids: readonly string[];
}

export interface TrackingFeedOptions {
  /** Also load, poll and stream the vehicles (Live View). */
  live: boolean;
}

export interface TrackingFeed {
  /** Every zone (the map's layer chips filter a copy; management always sees all). */
  geofences: GeofenceRow[];
  /**
   * LexiStar versioned geofence config available on the server (migration 021); the zone
   * Alerts switch (072, ST-00282).
   */
  geofenceVersioning: { versioning: boolean; versionHistory: boolean; alertSwitch: boolean };
  /** Map positions (`live` only). */
  vehicles: LiveVehiclePosition[];
  /** The /fleet overview behind the rail and the detail panel (`live` only). */
  fleet: FleetVehicle[];
  fleetLoading: boolean;
  /** Zone events that arrived live since the page opened, newest first (LexiStar). */
  events: ZoneEvent[];
  /** Pending Alerts: open zone alerts of the last 7 SAST days. */
  unacknowledgedCount: number;
  /** Events Today: zone events of today's SAST date. */
  eventsToday: number;
  loading: boolean;
  /** The first load shows a loading line; later refreshes keep the page as it is. */
  loadedOnce: boolean;
  /** Reload zones and the counts (and positions when `live`). */
  refresh: () => Promise<void>;
  /** Acknowledge one open zone alert; the acknowledged event, or null when it failed. */
  acknowledge: (eventId: string) => Promise<ZoneEvent | null>;
  /**
   * ST-00282: one Acknowledge all call (the server acknowledges at most 1000 per call; call again
   * with `remaining` while > 0). Throws the request's error (409 ALERTS_CHANGED included).
   */
  acknowledgeAll: (body: ZoneAlertsAcknowledgeAllBody) => Promise<ZoneAlertsAcknowledgeAllResponse>;
  /**
   * ST-00282: changes each time the server says alerts were acknowledged (here or anywhere in
   * the organisation, after the commit): lists re-read when it does.
   */
  acknowledgedHint: AcknowledgedHint;
}

export default function useTrackingFeed({ live }: TrackingFeedOptions): TrackingFeed {
  const [geofences, setGeofences] = useState<GeofenceRow[]>([]);
  const [geofenceVersioning, setGeofenceVersioning] = useState({
    versioning: false,
    versionHistory: false,
    alertSwitch: false,
  });
  const [acknowledgedHint, setAcknowledgedHint] = useState<AcknowledgedHint>({ nonce: 0, ids: [] });
  const [vehicles, setVehicles] = useState<LiveVehiclePosition[]>([]);
  const [events, setEvents] = useState<ZoneEvent[]>([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [eventsToday, setEventsToday] = useState(0);
  // The zones, for naming a live event's zone (the socket handler outlives renders), and the
  // live events already counted (the relay may deliver one twice).
  const zonesRef = useRef<GeofenceRow[]>([]);
  const seenRef = useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [fleet, setFleet] = useState<FleetVehicle[]>([]);
  const [fleetLoading, setFleetLoading] = useState(live);

  const applySummary = useCallback((body: ZoneEventSummaryResponse | undefined) => {
    setUnacknowledgedCount(body?.pendingAlerts || 0);
    setEventsToday(body?.eventsToday || 0);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [geofenceRes, summaryRes, vehicleRes] = await Promise.all([
        api.geofences.list(),
        api.geofenceEvents.summary(),
        live ? api.vehicleLocations.getLive() : Promise.resolve(null),
      ]);
      const geofenceBody = geofenceRes.data as GeofenceListResponse | undefined;
      zonesRef.current = geofenceBody?.data || [];
      setGeofences(geofenceBody?.data || []);
      setGeofenceVersioning({
        versioning: geofenceBody?.versioning === true,
        versionHistory: geofenceBody?.versionHistory === true,
        alertSwitch: geofenceBody?.alertSwitch === true,
      });
      applySummary(summaryRes.data as ZoneEventSummaryResponse | undefined);
      if (vehicleRes) {
        setVehicles((vehicleRes.data as LiveVehiclesResponse | undefined)?.data || []);
      }
    } catch (err) {
      console.error('Error fetching geofencing data:', err);
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, [live, applySummary]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The counts every 30 s (not on mount: refresh loads them). They follow alerts acknowledged
  // elsewhere, and the date past midnight; between polls live events count up.
  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.geofenceEvents.summary();
      applySummary(res.data as ZoneEventSummaryResponse | undefined);
    } catch (err) {
      console.error('Error fetching zone event counts:', err);
    }
  }, [applySummary]);
  usePolling(fetchSummary, TRACKING_POLL_MS, { immediate: false });

  // ST-00282: events acknowledged (committed) here or elsewhere: the live events show it.
  const fetchSummaryRef = useRef(fetchSummary);
  fetchSummaryRef.current = fetchSummary;
  const markAcknowledged = useCallback((ids: readonly string[]) => {
    if (!ids.length) return;
    const set = new Set(ids);
    setEvents((prev) =>
      prev.some((e) => set.has(e.id) && e.status === 'open')
        ? prev.map((e) => (set.has(e.id) && e.status === 'open' ? { ...e, status: 'acknowledged' } : e))
        : prev
    );
  }, []);

  // Rich fleet overview for the vehicle rail + detail panel. The 30 s cadence is
  // patched live between polls by the socket handler below.
  const fetchFleet = useCallback(async () => {
    try {
      const res = await api.vehicleLocations.getFleet();
      setFleet((res.data as FleetResponse | undefined)?.data || []);
    } catch (err) {
      console.error('Error fetching fleet overview:', err);
    } finally {
      setFleetLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!live) return;
    setFleetLoading(true);
    fetchFleet();
  }, [live, fetchFleet]);

  // Paused while this tab is in the background; refreshes on return.
  usePolling(fetchFleet, TRACKING_POLL_MS, { enabled: live });

  // Map markers: the socket moves them live; this 30 s poll is the fallback so
  // they still move if the socket is down (TRK-5).
  const fetchLive = useCallback(async () => {
    try {
      const res = await api.vehicleLocations.getLive();
      setVehicles((res.data as LiveVehiclesResponse | undefined)?.data || []);
    } catch (err) {
      console.error('Error fetching live positions:', err);
    }
  }, []);
  usePolling(fetchLive, TRACKING_POLL_MS, { enabled: live });

  // Socket.IO connection for real-time updates. Same origin as the API (the
  // proxy forwards /socket.io); the old `${hostname}:5002` URL never connected
  // behind the production proxy, so markers froze until a refresh (ST-00176).
  // The server authenticates the session cookie and joins this user's own
  // tenant room — the page does not name a tenant.
  useEffect(() => {
    const socket = connectSocket('/tracking');

    if (live) {
      socket.on('vehicle:position', (data: VehiclePositionMessage) => {
        setVehicles((prev) => {
          const idx = prev.findIndex((v) => v.regNo === data.regNo);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], ...data };
            return updated;
          }
          return [...prev, data];
        });
        // Keep the rail/detail-panel position fresh between 30 s polls. Only patch
        // the light live fields; status/telemetry/todayKm wait for the next poll.
        setFleet((prev) => {
          const idx = prev.findIndex((v) => v.regNo === data.regNo);
          if (idx < 0) return prev; // unknown vehicle — next poll will pick it up
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx]!,
            lat: data.lat ?? updated[idx]!.lat,
            lng: data.lng ?? updated[idx]!.lng,
            speed: data.speed ?? updated[idx]!.speed,
            heading: data.heading ?? updated[idx]!.heading,
            ts: data.ts ?? updated[idx]!.ts,
          };
          return updated;
        });
      });
    }

    // LexiStar events for this tenant (the relay, on the same /tracking tenant room): the zone
    // events among them are listed and counted as they arrive, each once.
    socket.on('lexistar:event', (message: LexiStarEventMessage) => {
      if (!isZoneEventMessage(message) || seenRef.current.has(message.id)) return;
      seenRef.current.add(message.id);
      if (seenRef.current.size > 500) {
        seenRef.current.delete(seenRef.current.values().next().value as string);
      }
      const event = zoneEventFromLive(message, zonesRef.current);
      setEvents((prev) => [event, ...prev].slice(0, 100));
      if (sastDayOf(event.eventTime) === sastDayOf(Date.now())) setEventsToday((n) => n + 1);
      if (isPendingAlert(event)) setUnacknowledgedCount((n) => n + 1);
    });

    // ST-00282: alerts acknowledged anywhere in the organisation (single or Acknowledge all),
    // sent after the commit: a hint to re-read the counts and the Events tab's list.
    socket.on('lexistar:events_acknowledged', (message: EventsAcknowledgedMessage) => {
      if (!message || !Array.isArray(message.ids)) return;
      markAcknowledged(message.ids);
      setAcknowledgedHint((prev) => ({ nonce: prev.nonce + 1, ids: message.ids }));
      fetchSummaryRef.current();
    });

    return () => {
      socket.disconnect();
    };
  }, [live, markAcknowledged]);

  // LexiStar's acknowledge (open -> acknowledged), for a zone alert of this tenant.
  const acknowledge = useCallback(async (eventId: string): Promise<ZoneEvent | null> => {
    try {
      const res = await api.geofenceEvents.acknowledge(eventId);
      const acknowledged = (res.data as ZoneEventAcknowledgeResponse | undefined)?.data ?? null;
      setEvents((prev) =>
        prev.map((e) => (e.id === eventId ? { ...e, status: 'acknowledged' } : e))
      );
      setUnacknowledgedCount((prev) => Math.max(0, prev - 1));
      return acknowledged;
    } catch (err) {
      console.error('Error acknowledging zone alert:', err);
      return null;
    }
  }, []);

  // ST-00282: Acknowledge all — the server decides what is acknowledged; the counts are re-read.
  const acknowledgeAll = useCallback(
    async (body: ZoneAlertsAcknowledgeAllBody): Promise<ZoneAlertsAcknowledgeAllResponse> => {
      const res = await api.geofenceEvents.acknowledgeAll(body);
      const data = res.data as ZoneAlertsAcknowledgeAllResponse;
      markAcknowledged(data?.ids || []);
      // The counts follow in the background: the list need not wait for them.
      void fetchSummaryRef.current();
      return data;
    },
    [markAcknowledged]
  );

  return {
    geofences,
    geofenceVersioning,
    vehicles,
    fleet,
    fleetLoading,
    events,
    unacknowledgedCount,
    eventsToday,
    loading,
    loadedOnce,
    refresh,
    acknowledge,
    acknowledgeAll,
    acknowledgedHint,
  };
}
